import type { Guest } from "@/types/Guest";

/**
 * Put ONE row back where it was, leaving every other row alone.
 *
 * The optimistic rollback on the requests providers used to be
 * `setRows(snapshot)` — the whole array as it looked before *this* decision
 * started. With two decisions in flight that is wrong in a way no error ever
 * surfaces: B succeeds and leaves the list, A fails and restores an array that
 * still contains B, so a request the server has already resolved reappears as
 * actionable and the next click writes a contradictory decision over it.
 *
 * Shared by both requests providers on purpose. The same rule kept in two
 * copies is the rule that drifts.
 *
 * @param index where the row sat in the pre-mutation snapshot. Clamped, so a
 *              list that shrank underneath still gets a sane insertion point.
 */
export function restoreRow(rows: Guest[], row: Guest, index: number): Guest[] {
  if (rows.some((g) => g.guestId === row.guestId)) {
    return rows.map((g) => (g.guestId === row.guestId ? row : g));
  }
  const at = Math.max(0, Math.min(index, rows.length));
  return [...rows.slice(0, at), row, ...rows.slice(at)];
}

/**
 * The only fields a nested guest DTO actually carries.
 *
 * `GuestRepository` builds `companionsList` and `mainGuestDTO` with just `_id`,
 * `firstName` and `lastName`, so a reconcile watch on anything else starts with
 * `fromRaw === undefined` and can never decide the value changed — the scheduler
 * polls to its deadline and gives up silently.
 *
 * Shared by both requests providers on purpose: this rule already existed in one
 * copy only, and the copy that lacked it rebuilt the exact defect it prevents.
 */
export const COMPANION_DTO_FIELDS = new Set(["guestId", "firstName", "lastName"]);
